import React, { useState, useRef, useEffect, useCallback } from 'react';
import './App.css';

const VIBE_OPTIONS = [
  { value: 1, emoji: '😤', label: 'Rancid' },
  { value: 2, emoji: '😒', label: 'Rough' },
  { value: 3, emoji: '😐', label: 'Meh' },
  { value: 4, emoji: '😊', label: 'Good vibes' },
  { value: 5, emoji: '🥰', label: 'Immaculate' },
];

const HANG_AGAIN_OPTIONS = [
  { value: 'Yes!!', emoji: '🙌', bg: '#dcf5e4', border: '#22c55e' },
  { value: 'Yeah I guess', emoji: '🤷', bg: '#dff0fb', border: '#3b82f6' },
  { value: 'Maybe', emoji: '🤔', bg: '#fef9c3', border: '#eab308' },
  { value: 'No', emoji: '🙅', bg: '#fee2e2', border: '#ef4444' },
  { value: 'I hate this guy now', emoji: '😤', bg: '#ffedd5', border: '#f97316' },
];

const STEPS = ['intro', 'name', 'vibe', 'hang-again', 'advice', 'selfie', 'thanks'];

const BG_COLORS = {
  intro: '#FFE8F0',
  name: '#E8F9F7',
  vibe: '#FFF4E8',
  'hang-again': '#EAF4FF',
  advice: '#F2FFE8',
  selfie: '#F4EBFF',
  thanks: '#FFE8FF',
};

// ─────────────────────────────────────────────
// Main App
// ─────────────────────────────────────────────
function App() {
  const [stepIndex, setStepIndex] = useState(0);
  const [animState, setAnimState] = useState('idle');
  const [formData, setFormData] = useState({
    name: '',
    vibeRating: null,
    hangAgain: null,
    advice: '',
    selfie: null,
  });
  const [selfiePreview, setSelfiePreview] = useState(null);
  // submitStep: null | 'uploading' | 'submitting'
  const [submitStep, setSubmitStep] = useState(null);

  // fileInputRef no longer needed — SelfieSlide uses label+id targeting
  const navTimerRef = useRef(null);
  const currentStep = STEPS[stepIndex];

  const navigate = useCallback(
    (dir) => {
      if (animState !== 'idle') return;
      const newIndex = dir === 'forward' ? stepIndex + 1 : stepIndex - 1;
      if (newIndex < 0 || newIndex >= STEPS.length) return;

      setAnimState(`exit-${dir}`);
      clearTimeout(navTimerRef.current);
      navTimerRef.current = setTimeout(() => {
        setStepIndex(newIndex);
        setAnimState(`enter-${dir}`);
        navTimerRef.current = setTimeout(() => {
          setAnimState('idle');
        }, 380);
      }, 260);
    },
    [animState, stepIndex]
  );

  const goNext = useCallback(() => navigate('forward'), [navigate]);
  const goBack = useCallback(() => navigate('backward'), [navigate]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFormData((d) => ({ ...d, selfie: file }));
    const reader = new FileReader();
    reader.onload = (ev) => setSelfiePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (includeSelfie = true) => {
    let selfieUrl = null;

    // ── Step 1: upload selfie to S3 via presigned URL ──────────────────────
    if (includeSelfie && formData.selfie) {
      setSubmitStep('uploading');
      try {
        // Ask our Netlify function for a presigned PUT URL
        const res = await fetch('/.netlify/functions/get-upload-url', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filename: formData.selfie.name || 'selfie.jpg',
            contentType: formData.selfie.type || 'image/jpeg',
          }),
        });

        if (res.ok) {
          const { presignedUrl, publicUrl } = await res.json();

          // Upload the file directly to S3 — no AWS keys touch the browser
          const s3Res = await fetch(presignedUrl, {
            method: 'PUT',
            body: formData.selfie,
            headers: { 'Content-Type': formData.selfie.type || 'image/jpeg' },
          });

          if (s3Res.ok) {
            selfieUrl = publicUrl;
          } else {
            console.error('S3 PUT failed:', s3Res.status);
          }
        } else {
          console.error('get-upload-url failed:', res.status);
        }
      } catch (err) {
        // Non-fatal — form still submits, just without the photo URL
        console.error('Selfie upload error:', err);
      }
    }

    // ── Step 2: submit Netlify form (text fields + S3 URL if we got one) ───
    setSubmitStep('submitting');
    const data = new FormData();
    data.append('form-name', 'post-hangout-survey');
    data.append('name', formData.name);
    data.append('vibe-rating', String(formData.vibeRating || ''));
    data.append('hang-again', formData.hangAgain || '');
    data.append('advice', formData.advice);
    if (selfieUrl) {
      data.append('selfie-url', selfieUrl);
    }
    try {
      await fetch('/', { method: 'POST', body: data });
    } catch (err) {
      console.error('Form submit error:', err);
    }

    setSubmitStep(null);
    goNext();
  };

  const questionNum = stepIndex - 1;
  const totalQuestions = STEPS.length - 2;
  const showProgress = stepIndex > 0 && currentStep !== 'thanks';
  const showBack = stepIndex > 1 && currentStep !== 'thanks';
  const progress = totalQuestions > 0 ? (questionNum / totalQuestions) * 100 : 0;

  return (
    <div className="app" style={{ backgroundColor: BG_COLORS[currentStep] }}>
      <div className="bg-orb bg-orb-1" />
      <div className="bg-orb bg-orb-2" />

      {showProgress && (
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
      )}

      <div className={`slide slide-${animState}`}>
        {currentStep === 'intro' && <IntroSlide onStart={goNext} />}

        {currentStep === 'name' && (
          <NameSlide
            value={formData.name}
            onChange={(v) => setFormData((d) => ({ ...d, name: v }))}
            onNext={goNext}
          />
        )}

        {currentStep === 'vibe' && (
          <VibeSlide
            value={formData.vibeRating}
            onChange={(v) => setFormData((d) => ({ ...d, vibeRating: v }))}
            onNext={goNext}
          />
        )}

        {currentStep === 'hang-again' && (
          <HangAgainSlide
            value={formData.hangAgain}
            onChange={(v) => setFormData((d) => ({ ...d, hangAgain: v }))}
            onNext={goNext}
          />
        )}

        {currentStep === 'advice' && (
          <AdviceSlide
            value={formData.advice}
            onChange={(v) => setFormData((d) => ({ ...d, advice: v }))}
            onNext={goNext}
          />
        )}

        {currentStep === 'selfie' && (
          <SelfieSlide
            selfiePreview={selfiePreview}
            onChange={handleFileChange}
            onSubmit={() => handleSubmit(true)}
            onSkip={() => handleSubmit(false)}
            submitStep={submitStep}
          />
        )}

        {currentStep === 'thanks' && <ThanksSlide name={formData.name} />}
      </div>

      {showBack && (
        <button
          className="back-btn"
          onClick={goBack}
          disabled={animState !== 'idle'}
        >
          ← back
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Slide Components
// ─────────────────────────────────────────────

function IntroSlide({ onStart }) {
  return (
    <div className="slide-content intro-slide">
      <div className="intro-emoji">🍻</div>
      <h1 className="intro-title">
        Kirt's Post<br />Hangout Survey
      </h1>
      <p className="intro-subtitle">
        Express how much you hated this.
      </p>
      <button className="btn-primary btn-large" onClick={onStart}>
        Let's go →
      </button>
    </div>
  );
}

function NameSlide({ value, onChange, onNext }) {
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 400);
    return () => clearTimeout(t);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && value.trim()) onNext();
  };

  return (
    <div className="slide-content">
      <div className="question-label">Question 1 of 4</div>
      <h2 className="question-title">What's your name?</h2>
      <input
        ref={inputRef}
        className="text-input"
        type="text"
        placeholder="Type your name..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        autoComplete="given-name"
      />
      <button className="btn-primary" onClick={onNext} disabled={!value.trim()}>
        Continue →
      </button>
    </div>
  );
}

function VibeSlide({ value, onChange, onNext }) {
  const timerRef = useRef(null);
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleSelect = (v) => {
    onChange(v);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onNextRef.current(), 700);
  };

  return (
    <div className="slide-content">
      <div className="question-label">Question 2 of 4</div>
      <h2 className="question-title">How would you rate<br />the vibe of this hang?</h2>
      <div className="emoji-scale">
        {VIBE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            className={`emoji-btn ${value === opt.value ? 'selected' : ''}`}
            onClick={() => handleSelect(opt.value)}
          >
            <span className="emoji-icon">{opt.emoji}</span>
            <span className="emoji-label">{opt.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function HangAgainSlide({ value, onChange, onNext }) {
  const timerRef = useRef(null);
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleSelect = (v) => {
    onChange(v);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onNextRef.current(), 700);
  };

  return (
    <div className="slide-content">
      <div className="question-label">Question 3 of 4</div>
      <h2 className="question-title">Would you hang out again?</h2>
      <div className="choice-list">
        {HANG_AGAIN_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            className={`choice-btn ${value === opt.value ? 'selected' : ''}`}
            style={{ '--btn-bg': opt.bg, '--btn-border': opt.border }}
            onClick={() => handleSelect(opt.value)}
          >
            <span className="choice-emoji">{opt.emoji}</span>
            <span className="choice-label">{opt.value}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function AdviceSlide({ value, onChange, onNext }) {
  return (
    <div className="slide-content">
      <div className="question-label">Question 4 of 4</div>
      <h2 className="question-title">
        Small words of advice<br />to improve the vibes?
      </h2>
      <textarea
        className="text-input textarea"
        placeholder="e.g. more snacks, better playlist, less talking about work..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        maxLength={280}
      />
      <button className="btn-primary" onClick={onNext}>
        {value.trim() ? 'Looks good →' : 'Skip →'}
      </button>
    </div>
  );
}

function SelfieSlide({ selfiePreview, onChange, onSubmit, onSkip, submitStep }) {
  const isSubmitting = submitStep !== null;
  const submitLabel =
    submitStep === 'uploading'  ? '📤 Uploading photo...' :
    submitStep === 'submitting' ? '✨ Submitting...'      :
                                  'Submit →';

  return (
    <div className="slide-content">
      <div className="question-label">Optional</div>
      <h2 className="question-title">Attach a selfie that<br />captures your mood 📸</h2>

      {/*
        Two separate inputs:
        - selfie-camera: capture="user" → opens front-facing camera directly on mobile
        - selfie-gallery: no capture → opens the photo library / file picker
      */}
      <input
        type="file"
        accept="image/*"
        capture="user"
        onChange={onChange}
        style={{ display: 'none' }}
        id="selfie-camera"
      />
      <input
        type="file"
        accept="image/*"
        onChange={onChange}
        style={{ display: 'none' }}
        id="selfie-gallery"
      />

      {selfiePreview ? (
        <div className="selfie-preview">
          <img src={selfiePreview} alt="Your selfie" className="selfie-img" />
          <label htmlFor="selfie-camera" className="btn-secondary" style={{ cursor: 'pointer' }}>
            Retake 🔄
          </label>
        </div>
      ) : (
        <div className="selfie-options">
          <label htmlFor="selfie-camera" className="selfie-option-btn selfie-option-camera">
            <span className="selfie-option-icon">📸</span>
            <span className="selfie-option-text">Take a selfie</span>
          </label>
          <label htmlFor="selfie-gallery" className="selfie-option-btn selfie-option-gallery">
            <span className="selfie-option-icon">🖼️</span>
            <span className="selfie-option-text">Choose from library</span>
          </label>
        </div>
      )}

      <div className="btn-group">
        <button className="btn-primary" onClick={onSubmit} disabled={isSubmitting}>
          {submitLabel}
        </button>
        {!selfiePreview && (
          <button className="btn-ghost" onClick={onSkip} disabled={isSubmitting}>
            Skip photo
          </button>
        )}
      </div>
    </div>
  );
}

function ThanksSlide({ name }) {
  const firstName = name ? name.trim().split(' ')[0] : null;

  return (
    <div className="slide-content thanks-slide">
      <div className="thanks-emoji">🎉</div>
      <h2 className="thanks-title">
        {firstName ? `Thanks, ${firstName}!` : "You're the best!"}
      </h2>
      <p className="thanks-subtitle">
        Your feedback has been received.<br />The vibes appreciate you. ✨
      </p>
    </div>
  );
}

export default App;
